import axios from 'axios';

const getAuthToken = (): string | null => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session_token') {
      return value;
    }
  }
  return null;
};

const BFF_API_URL = import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086';
const AUTH_APP_URL = import.meta.env.VITE_AUTH_APP_URL || 'http://localhost:3000';
const pmsClient = axios.create({
  baseURL: BFF_API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

pmsClient.interceptors.request.use(config => {
  const token = getAuthToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

pmsClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      window.location.href = AUTH_APP_URL;
    }
    return Promise.reject(error);
  },
);

// --- Types ---
export type RoomStatus =
  | 'available'
  | 'occupied'
  | 'maintenance'
  | 'cleaning'
  | 'reserved'
  | 'out_of_service';
export type MaintenancePriority = 'low' | 'medium' | 'high' | 'critical' | 'emergency';
export type MaintenanceStatus = 'reported' | 'in_progress' | 'on_hold' | 'resolved' | 'cancelled';

export interface MealPlanPriceItem {
  meal_plan_id: string;
  price: number;
}

export interface RoomType {
  id: string;
  name: string;
  code: string;
  base_rate: number;
  hourly_rate?: number;
  max_occupancy: number;
  default_amenities?: string[];
  size_sqm?: number;
  bed_configuration?: string;
  images?: string[];
  description?: string;
  season?: {
    base_rate: number;
    hourly_rate?: number;
    meal_plan?: MealPlanPriceItem[];
  };
  created_at: string;
  updated_at: string;
}

export interface Room {
  id: string;
  room_number: string;
  floor: number;
  wing?: string;
  room_type_id: string;
  room_type?: RoomType;
  status: RoomStatus;
  size_sqm?: number;
  view_description?: string;
  current_rate?: number;
  features?: Record<string, unknown>;
  last_cleaned_at?: string;
  last_maintenance_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceLog {
  id: string;
  room_id: string;
  room?: Room;
  issue_type: string;
  description: string;
  priority: MaintenancePriority;
  status: MaintenanceStatus;
  reported_by?: string;
  assigned_to?: string;
  resolution_notes?: string;
  resolution_photos?: string[];
  estimated_cost?: number;
  actual_cost?: number;
  reported_at: string;
  scheduled_at?: string;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
}

export interface PaginatedRooms {
  rooms: Room[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PaginatedRoomTypes {
  room_types: RoomType[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface PaginatedMaintenanceLogs {
  maintenance_logs: MaintenanceLog[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface OccupancyStats {
  total_rooms: number;
  occupied_rooms: number;
  available_rooms: number;
  maintenance: number;
  cleaning: number;
  reserved: number;
  occupancy_rate: number;
}

// --- Room Service ---
export interface RoomListParams {
  page?: number;
  page_size?: number;
  status?: RoomStatus;
}

export interface RoomAvailableParams {
  status?: RoomStatus;
}

export const roomService = {
  list: async (params?: RoomListParams): Promise<PaginatedRooms> => {
    const res = await pmsClient.get<PaginatedRooms>('/api/v1/rooms', { params });
    return res.data;
  },
  getAvailable: async (params?: RoomAvailableParams): Promise<PaginatedRooms> => {
    const res = await pmsClient.get<PaginatedRooms>('/api/v1/rooms/available', { params });
    return res.data;
  },
  getOccupancyStats: async (): Promise<OccupancyStats> => {
    const res = await pmsClient.get<OccupancyStats>('/api/v1/rooms/stats/occupancy');
    return res.data;
  },
  get: (id: string) => pmsClient.get<Room>(`/api/v1/rooms/${id}`),
  getByNumber: (number: string) => pmsClient.get<Room>(`/api/v1/rooms/number/${number}`),
  create: (data: Partial<Room>) => pmsClient.post<Room>('/api/v1/rooms', data),
  update: (id: string, data: Partial<Room>) => pmsClient.put<Room>(`/api/v1/rooms/${id}`, data),
  updateStatus: (id: string, status: RoomStatus) =>
    pmsClient.patch<Room>(`/api/v1/rooms/${id}/status`, { status }),
  delete: (id: string) => pmsClient.delete(`/api/v1/rooms/${id}`),
};

// --- Room Type Service ---
export interface RoomTypeListParams {
  page?: number;
  page_size?: number;
}

export interface MealPlan {
  id: string;
  name: string;
  code: string;
  description?: string;
  is_complementary: boolean;
  is_active: boolean;
}

export interface RoomTypeMealPlan {
  id: string;
  room_type_id: string;
  meal_plan_id: string;
  price: number;
  meal_plan?: MealPlan;
}

export interface SeasonalPricing {
  id: string;
  room_type_id: string;
  meal_plan_id?: string;
  meal_plan_prices_json?: { meal_plan_id: string; price: number }[];
  price: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

export interface CalculatePriceParams {
  room_type_id: string;
  meal_plan_id?: string;
  date: string;
}

export interface CalculatePriceResponse {
  price: number;
}

export const roomTypeService = {
  list: async (params?: RoomTypeListParams): Promise<PaginatedRoomTypes> => {
    const res = await pmsClient.get<PaginatedRoomTypes>('/api/v1/room-types', { params });
    return res.data;
  },
  get: (id: string) => pmsClient.get<RoomType>(`/api/v1/room-types/${id}`),
  create: (data: Partial<RoomType>) => pmsClient.post<RoomType>('/api/v1/room-types', data),
  update: (id: string, data: Partial<RoomType>) =>
    pmsClient.put<RoomType>(`/api/v1/room-types/${id}`, data),
  delete: (id: string) => pmsClient.delete(`/api/v1/room-types/${id}`),
  // Meal Plans
  listMealPlans: () => pmsClient.get<MealPlan[]>('/api/v1/meal-plans'),
  createMealPlan: (data: Partial<MealPlan>) => pmsClient.post<MealPlan>('/api/v1/meal-plans', data),
  // Room Type Meal Plans
  getRoomTypeMealPlans: (roomTypeId: string) =>
    pmsClient.get<RoomTypeMealPlan[]>(`/api/v1/room-types/${roomTypeId}/meal-plans`),
  setRoomTypeMealPlan: (roomTypeId: string, data: { meal_plan_id: string; price: number }) =>
    pmsClient.post<RoomTypeMealPlan>(`/api/v1/room-types/${roomTypeId}/meal-plans`, data),
  // Seasonal Pricing
  getSeasonalPricing: (roomTypeId: string) =>
    pmsClient.get<SeasonalPricing[]>(`/api/v1/room-types/${roomTypeId}/seasonal-pricing`),
  getAllSeasonalPricing: () =>
    pmsClient.get<SeasonalPricing[]>(`/api/v1/room-types/seasonal-pricing`),
  createSeasonalPricing: (
    roomTypeId: string,
    data: {
      meal_plan_id?: string;
      meal_plan_prices?: { meal_plan_id: string; price: number }[];
      price: number;
      hourly_rate?: number;
      start_date: string;
      end_date: string;
    },
  ) => pmsClient.post<SeasonalPricing>(`/api/v1/room-types/${roomTypeId}/seasonal-pricing`, data),
  updateSeasonalPricing: (
    id: string,
    data: {
      meal_plan_id?: string;
      price?: number;
      hourly_rate?: number;
      start_date?: string;
      end_date?: string;
      is_active?: boolean;
    },
  ) => pmsClient.put<SeasonalPricing>(`/api/v1/room-types/seasonal-pricing/${id}`, data),
  deleteSeasonalPricing: (id: string) =>
    pmsClient.delete(`/api/v1/room-types/seasonal-pricing/${id}`),
  // Calculate price
  calculatePrice: (params: CalculatePriceParams) =>
    pmsClient.post<CalculatePriceResponse>('/api/v1/room-types/calculate-price', params),
};

// --- Maintenance Service ---
export interface MaintenanceListParams {
  page?: number;
  page_size?: number;
  room_id?: string;
  status?: MaintenanceStatus;
}

export const maintenanceService = {
  list: async (params?: MaintenanceListParams): Promise<PaginatedMaintenanceLogs> => {
    const res = await pmsClient.get<PaginatedMaintenanceLogs>('/api/v1/maintenance', { params });
    return res.data;
  },
  getActive: async (params?: {
    page?: number;
    page_size?: number;
  }): Promise<PaginatedMaintenanceLogs> => {
    const res = await pmsClient.get<PaginatedMaintenanceLogs>('/api/v1/maintenance/active', {
      params,
    });
    return res.data;
  },
  get: (id: string) => pmsClient.get<MaintenanceLog>(`/api/v1/maintenance/${id}`),
  create: (data: Partial<MaintenanceLog>) =>
    pmsClient.post<MaintenanceLog>('/api/v1/maintenance', data),
  update: (id: string, data: Partial<MaintenanceLog>) =>
    pmsClient.put<MaintenanceLog>(`/api/v1/maintenance/${id}`, data),
  updateStatus: (id: string, status: MaintenanceStatus) =>
    pmsClient.patch<MaintenanceLog>(`/api/v1/maintenance/${id}/status`, { status }),
  delete: (id: string) => pmsClient.delete(`/api/v1/maintenance/${id}`),
};

// --- Housekeeping ---
export type CleaningTaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export interface Housekeeper {
  id: string;
  first_name: string;
  last_name: string;
  phone?: string;
  email?: string;
  is_active: boolean;
  shift: string;
  joined_at: string;
  created_at: string;
  updated_at: string;
}

export interface CleaningTask {
  id: string;
  room_id: string;
  housekeeper_id?: string | null;
  status: CleaningTaskStatus | string;
  priority: number;
  started_at?: string | null;
  completed_at?: string | null;
  notes?: string;
  created_at: string;
  updated_at: string;
  room?: Room;
  housekeeper?: Housekeeper;
}

export const housekeeperService = {
  list: async (): Promise<Housekeeper[]> => {
    const res = await pmsClient.get<{ housekeepers: Housekeeper[] }>('/api/v1/housekeepers');
    return res.data.housekeepers || [];
  },
  create: (data: Partial<Housekeeper>) => pmsClient.post<Housekeeper>('/api/v1/housekeepers', data),
  update: (id: string, data: Partial<Housekeeper>) =>
    pmsClient.put<Housekeeper>(`/api/v1/housekeepers/${id}`, data),
  delete: (id: string) => pmsClient.delete(`/api/v1/housekeepers/${id}`),
};

export const cleaningTaskService = {
  list: async (): Promise<CleaningTask[]> => {
    const res = await pmsClient.get<{ tasks: CleaningTask[] }>('/api/v1/cleaning-tasks');
    return res.data.tasks || [];
  },
  create: (data: { room_id: string; priority?: number }) =>
    pmsClient.post<CleaningTask>('/api/v1/cleaning-tasks', data),
  assign: (id: string, housekeeper_id: string) =>
    pmsClient.patch<CleaningTask>(`/api/v1/cleaning-tasks/${id}/assign`, { housekeeper_id }),
  start: (id: string) => pmsClient.patch<CleaningTask>(`/api/v1/cleaning-tasks/${id}/start`),
  complete: (id: string) => pmsClient.patch<CleaningTask>(`/api/v1/cleaning-tasks/${id}/complete`),
};

// --- Export aggregate ---
export const pmsApi = {
  roomService,
  roomTypeService,
  maintenanceService,
  housekeeperService,
  cleaningTaskService,
};

export default pmsApi;
